// =========================================================================
// Platform-admin SMS settings + per-user phone read.
//
// Settings table read (direct) + write via rpc_admin_upsert_sms_settings.
// =========================================================================

import { supabase } from '../client';

export interface SmsSettings {
  id: number;
  enabled: boolean;
  default_from_number: string | null;
  default_signature: string | null;
  platform_webhook_secret: string | null;
  cost_cents_per_sms_segment: number;
  cost_cents_per_imessage: number;
  cost_cents_per_mms: number;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
  archived_at: string | null;
}

export async function getSmsSettings(): Promise<SmsSettings | null> {
  const { data, error } = await supabase()
    .schema('core')
    .from('sms_settings')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as SmsSettings | null) ?? null;
}

export interface UpsertSmsSettingsInput {
  enabled?: boolean;
  default_from_number?: string | null;
  default_signature?: string | null;
  platform_webhook_secret?: string | null;
  cost_cents_per_sms_segment?: number;
  cost_cents_per_imessage?: number;
  cost_cents_per_mms?: number;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  notes?: string | null;
}

export async function upsertSmsSettings(input: UpsertSmsSettingsInput): Promise<SmsSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_admin_upsert_sms_settings', {
      p_enabled: input.enabled ?? undefined,
      p_default_from_number: input.default_from_number ?? undefined,
      p_default_signature: input.default_signature ?? undefined,
      p_platform_webhook_secret: input.platform_webhook_secret ?? undefined,
      p_cost_cents_per_sms_segment: input.cost_cents_per_sms_segment ?? undefined,
      p_cost_cents_per_imessage: input.cost_cents_per_imessage ?? undefined,
      p_cost_cents_per_mms: input.cost_cents_per_mms ?? undefined,
      p_quiet_hours_start: input.quiet_hours_start ?? undefined,
      p_quiet_hours_end: input.quiet_hours_end ?? undefined,
      p_notes: input.notes ?? undefined,
    });
  if (error) throw error;
  return data as unknown as SmsSettings;
}

// -------------------------------------------------------------------------
// Read current user's phone row -- for the Profile > Security page header
// -------------------------------------------------------------------------

export interface UserPhoneRow {
  user_id: string;
  phone: string;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function getMyPhone(): Promise<UserPhoneRow | null> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase()
    .schema('core')
    .from('user_phone_numbers')
    .select('user_id, phone, verified_at, created_at, updated_at')
    .eq('user_id', uid)
    .is('archived_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as UserPhoneRow | null) ?? null;
}

// -------------------------------------------------------------------------
// Account ID -- for displaying in the profile / opt-in screen
// -------------------------------------------------------------------------

export async function getMyAccountId(): Promise<string | null> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase()
    .schema('core')
    .from('users')
    .select('account_id')
    .eq('id', uid)
    .maybeSingle();
  if (error) throw error;
  return (data as { account_id?: string } | null)?.account_id ?? null;
}
